import { IStoreAssetApiResponse } from 'models/store/i-store-asset-api-response.interface';
import {
  IWorldNftTradeErrorResponse,
  IWorldNftTradeResponse,
  IWorldNftTradeSoldOutResponse,
  IWorldNftTradeSuccessResponse,
  IWorldNftTradeVariation,
} from 'models/store/i-world-nft-trade-response.interface';

import { INftCardAsset } from 'components/nftCard/_new/types';

import { getBaseVariation } from 'utils/trade-variations.utils';

/**
 * Type guard to check if response is success response with active sales
 */
export const isWorldNftTradeSuccess = (response: IWorldNftTradeResponse): response is IWorldNftTradeSuccessResponse => {
  return response.id !== null && response.world_nft !== null && 'available_trade_offers_by_variation' in response;
};

/**
 * Type guard to check if response is error response (asset not found)
 */
export const isWorldNftTradeError = (response: IWorldNftTradeResponse): response is IWorldNftTradeErrorResponse => {
  return response.id === null && response.world_nft === null;
};

/**
 * Type guard to check if response is sold out with asset data
 */
export const isWorldNftTradeSoldOut = (response: IWorldNftTradeResponse): response is IWorldNftTradeSoldOutResponse => {
  return (
    response.id !== null &&
    response.world_nft !== null &&
    'status' in response &&
    response.status === 'SOLD_OUT' &&
    !('available_trade_offers_by_variation' in response)
  );
};

/**
 * Get the first available variation data for pricing and availability
 * According to API docs: "używamy available_trade_offers_by_variation i bierzmy pierwszy obiekt z wewnątrz i go uzywamy jako 'base'"
 */
export const getBaseVariationData = (tradeData: IWorldNftTradeSuccessResponse): IWorldNftTradeVariation | null => {
  return getBaseVariation(tradeData.available_trade_offers_by_variation);
};

/**
 * Convert World NFT trade data and asset data to NftCardAsset format
 */
export const convertToNftCardAsset = (
  tradeData: IWorldNftTradeResponse,
  assetData: IStoreAssetApiResponse,
  expectedAnnualYield?: number | null
): INftCardAsset | null => {
  // Handle error response (asset not found)
  if (isWorldNftTradeError(tradeData)) {
    return null;
  }

  // Handle sold out response with asset data
  if (isWorldNftTradeSoldOut(tradeData)) {
    // Get category from traits if available
    const categoryTrait = assetData.traits.find((trait) => trait.type === 'category');

    return {
      id: tradeData.world_nft.token_id,
      category: categoryTrait?.value || 'building',
      animationUrl: assetData.animation_url,
      imageUrl: assetData.image_original_url,
      markerUrl: assetData.marker_url,
      shares: 0, // No shares available
      name: tradeData.world_nft.name,
      address: assetData.address,
      activeShares: 0,
      mintedShares: 0,

      // Optional fields with values from APIs
      ownedShares: assetData.owned_shares,
      totalOwners: 0,
      royalties: undefined,
      ownedEarnings: undefined,
      salesVolume: undefined,
      topOwners: undefined,
      expectedAnnualYield: expectedAnnualYield ? (expectedAnnualYield * 100).toString() : '0',
      price: 0, // No price when sold out
      isSoldOut: true,
    };
  }

  // Handle success response with active sales
  if (isWorldNftTradeSuccess(tradeData)) {
    // Get base variation data for pricing
    const baseVariation = getBaseVariationData(tradeData);
    if (!baseVariation) {
      return null;
    }

    const isSoldOut = baseVariation.available_trade_offers.length === 0;

    // Get category from traits if available
    const categoryTrait = assetData.traits.find((trait) => trait.type === 'category');

    return {
      id: tradeData.world_nft.token_id,
      category: categoryTrait?.value || 'building',
      animationUrl: assetData.animation_url,
      imageUrl: assetData.image_original_url,
      markerUrl: assetData.marker_url,
      shares: baseVariation.total_shares,
      name: tradeData.world_nft.name,
      address: assetData.address,
      activeShares: baseVariation.total_shares,
      mintedShares: baseVariation.total_shares,

      // Optional fields with values from APIs
      ownedShares: assetData.owned_shares,
      totalOwners: 0, // TODO: This would need to come from a different endpoint
      royalties: undefined,
      ownedEarnings: undefined,
      salesVolume: undefined,
      topOwners: undefined,
      expectedAnnualYield: expectedAnnualYield ? (expectedAnnualYield * 100).toString() : '0',
      price: Number(baseVariation.price),
      isSoldOut,
    };
  }

  // Unknown response type
  return null;
};
